import PrintSheet, { SheetTitle, TD, TH } from "@/modules/print/sheet";
import { SnapshotSchedule } from "@buildpy/core/modules/quote/types";

interface Props {
  schedule: SnapshotSchedule;
}

const WEEKDAY = ["일", "월", "화", "수", "목", "금", "토"] as const;

const DAY_MS = 86_400_000;

const utcOf = (iso: string): number => Date.parse(`${iso}T00:00:00Z`);

/** `2026-03-05` → `3월 5일(목)`. 형식이 아니면 그대로 둔다. */
const longDate = (iso: string): string => {
  const ms = utcOf(iso);
  if (!Number.isFinite(ms)) return iso;
  const d = new Date(ms);
  return `${d.getUTCMonth() + 1}월 ${d.getUTCDate()}일(${WEEKDAY[d.getUTCDay()]})`;
};

const shortDate = (iso: string): string => {
  const ms = utcOf(iso);
  if (!Number.isFinite(ms)) return iso;
  const d = new Date(ms);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
};

/** 양끝을 포함한 달력일 수. 막대 길이에만 쓴다. */
const spanDays = (from: string, to: string): number => {
  const a = utcOf(from);
  const b = utcOf(to);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.floor((b - a) / DAY_MS) + 1;
};

/**
 * 공정표. 고객이 「언제 시작해서 언제 끝나는가」 를 한 장에서 보는 자리다.
 *
 * 막대는 배경색 말고 아무것도 없어서, 그냥 두면 **인쇄에서 그 열이 통째로 빈다**
 * (브라우저가 기본으로 배경을 뺀다). `print-ink` 로 그 자리만 배경을 살린다.
 * 그래도 사용자가 인쇄 설정에서 배경을 끄면 빠지는데, 그때도 옆 칸의 날짜가
 * 그대로 있어 표는 읽힌다.
 */
const QuoteSchedule = ({ schedule }: Props) => {
  const total = spanDays(schedule.start_date, schedule.end_date);

  return (
    <PrintSheet>
      <SheetTitle>시공 일정</SheetTitle>

      <dl className="mb-3 flex flex-wrap gap-x-8 gap-y-1 text-sm">
        <div className="flex gap-x-2">
          <dt className="text-[#525252]">예상 착공일</dt>
          <dd className="font-medium">{longDate(schedule.start_date)}</dd>
        </div>
        <div className="flex gap-x-2">
          <dt className="text-[#525252]">예상 준공일</dt>
          <dd className="font-medium">{longDate(schedule.end_date)}</dd>
        </div>
        <div className="flex gap-x-2">
          <dt className="text-[#525252]">총 작업</dt>
          <dd className="font-medium">{schedule.total_workdays}일</dd>
        </div>
      </dl>

      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col className="w-[18%]" />
          <col className="w-[28%]" />
          <col className="w-[22%]" />
          <col />
        </colgroup>
        <thead>
          <tr>
            <TH>공정</TH>
            <TH>작업</TH>
            <TH>기간</TH>
            <TH>일정</TH>
          </tr>
        </thead>
        <tbody>
          {schedule.phases.map((phase, index) => {
            const length = spanDays(phase.start_date, phase.end_date);
            const offset = spanDays(schedule.start_date, phase.start_date) - 1;
            return (
              <tr key={`${phase.name}-${phase.start_date}-${index}`}>
                <TD className="whitespace-nowrap">{phase.name}</TD>
                <TD className="text-[#525252]">{phase.trades.join(", ")}</TD>
                <TD className="whitespace-nowrap tabular-nums">
                  {shortDate(phase.start_date)} ~ {shortDate(phase.end_date)}
                </TD>
                <TD>
                  <div className="print-ink relative h-2 w-full rounded bg-[#e5e5e5]">
                    <div
                      className="print-ink absolute inset-y-0 rounded bg-[#525252]"
                      style={{
                        left: `${total > 0 ? (offset / total) * 100 : 0}%`,
                        width: `${
                          total > 0 ? Math.max((length / total) * 100, 2) : 0
                        }%`,
                      }}
                    />
                  </div>
                </TD>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className="mt-3 text-xs text-[#737373]">
        일정은 예정이며 현장 사정에 따라 바뀔 수 있습니다.
        {!schedule.holidays_applied && " 공휴일은 반영되지 않았습니다."}
      </p>
    </PrintSheet>
  );
};

export default QuoteSchedule;
